import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FindLoad, FindSuccessItem, Images, PermissionList, RolePermission, SaveParams, useModal } from 'utils';
import { MinLoader, SelectTypeAutocomplete, SimpleTooltip, ToolInfo, CustomDelete, CustomizedSwitch } from 'components';
import { formActions } from 'store';
import { formTabEnums, transformNotesObject } from './constants';
import { useFormBuilder } from './formContext';
import { findUnnamedSectionIndex } from './section';
import { formBuilderStatusEnums as firmStatusEnums, formModelEnums, IntakeForm } from '../constants';
import { DataFields } from '../fragments';
import { VersionHistoryPanel } from './versionHistory/versionHistoryPanel';
import { EditWithIcon } from '../../../components';

export const FormDetailHeader = ({ onPreview, onStopPreview, onRestored }) => {
    const { formList, setFormList, setFormListRaw, resetHistory, error, setError, isDirty, setIsDirty, previewing, setPreviewing, initialFormListStr, undo, redo, canUndo, canRedo, setActiveSection } = useFormBuilder();
    const ACTION_TYPE = formList?.id ? 'EDIT_FORM' : 'CREATE_FORM';
    const formSettings = useSelector((state) => state.forms.formSettings);
    const form = useSelector((state) => state.forms.form);
    const titleRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const activeTab = info?.activeTab || formTabEnums.BUILDER;
    const dispatch = useDispatch();
    const [showHistory, setShowHistory] = useState(false);
    const checkDefault = form?.id ? form?.default : false;
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const models = formSettings?.models || [];
    const { open, close } = useModal();

    useEffect(() => {
        if (success?.type && success?.data?.data?.id) {
            initialFormListStr.current = JSON.stringify(formList);
            setIsDirty(false);
            setTimeout(() => {
                navigate(`/template/${success?.data?.data?.id}`, { replace: true });
            }, 50);
        }
    }, [success]);
    const handleChange = (e) => {
        if (error) {
            setError('');
        }
        if (e?.target?.name === 'model') {
            if (e?.target?.value) {
                dispatch(formActions.getForms({ model: e?.target?.value }));
            } else {
                dispatch(formActions.resetForms());
            }

            const params = {
                ...formList,
            };

            if (e?.target?.value === formModelEnums.INTAKEFORM) {
                params[e.target.name] = e.target.value;
                params.sections = IntakeForm;
            } else {
                params[e.target.name] = e.target.value;
                params.sections = params.sections?.map((i) => ({ ...i, attributes: [] }));
            }

            setFormList(params);
        } else {
            setFormList((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
        }
    };


    const changeActiveTab = (tab) => {
        const params = {
            ...info,
        };
        params.activeTab = tab;
        SaveParams(navigate, params);
    };

    const submitForm = async () => {
        // Block saving while previewing a historical version (read-only mode).
        if (previewing) return;
        // Open the unnamed section's settings, where its required name is flagged.
        const unnamedIndex = findUnnamedSectionIndex(formList?.sections);
        if (unnamedIndex !== -1) {
            setActiveSection(unnamedIndex + 1);
            return;
        }
        const status = formList?.status || firmStatusEnums.PUBLISHED;
        if (formList?.title && formList?.model) {
            const payload = { ...formList, status };
            payload.status = formList?.status ? formList?.status : firmStatusEnums.UNPUBLISHED;
            if (formList?.id) {
                // Versioning is handled on the backend: the save itself diffs the
                // stored form against this payload and records a version.
                dispatch(formActions.editForm(formList?.id, payload));
                initialFormListStr.current = JSON.stringify(payload);
                setIsDirty(false);
            } else {
                dispatch(formActions.createForm(payload));
            }
        } else {
            setError(!formList?.title && !formList?.model ? 'all' : !formList?.title ? 'title' : !formList?.model ? 'model' : '');

            if (!formList?.title) {
                titleRef.current?.focus();
            }
        }
    };

    const handleStatusToggle = () => {
        const newStatus = formList?.status === firmStatusEnums.PUBLISHED
            ? firmStatusEnums.UNPUBLISHED
            : firmStatusEnums.PUBLISHED;
        setFormList((prevState) => ({ ...prevState, status: newStatus }));
    };

    return (
        <>
            <div className="form-detail-header-box">
                <div className="form-header-left">
                    <button
                        onClick={() => {
                            if (isDirty) {
                                open(
                                    <CustomDelete
                                        info="Unsaved Changes"
                                        text="You have unsaved changes. Are you sure you want to leave this page?"
                                        innerText="Discard Changes"
                                        color="blue"
                                        handleDel={() => {
                                            close();
                                            setIsDirty(false);
                                            setTimeout(() => navigate('/templates'), 50);
                                        }}
                                        handleClose={() => close()}
                                        noClose={true}
                                    />
                                );
                            } else {
                                navigate('/templates');
                            }
                        }}
                        className="form-back-btn-box">
                        <img src={Images.backBtn} alt="back-btn" />
                        <p>Back to Templates</p>
                    </button>
                    <div className="gray-line" />

                    <div className="flex-align-start full-width" style={{ gap: 8 }}>
                        <div style={{ marginTop: 2 }}>
                            <ToolInfo
                                text={
                                    'This title helps identify the template within the system. It won’t be visible to users filling out the form.'
                                }
                            />
                        </div>
                        <div className="form-heading-info-box" style={{ width: '80%' }}>
                            <div className="form-heading">
                                <input
                                    type="text"
                                    name={'title'}
                                    placeholder={'Internal Title *'}
                                    value={formList?.title ? formList?.title : ''}
                                    onChange={handleChange}
                                    ref={titleRef}
                                />
                            </div>
                            {(error === 'title' || error === 'all') && <h6>Please enter the internal template title</h6>}
                            <div className="form-description">
                                <input
                                    type="text"
                                    name={'description'}
                                    placeholder={'Internal Description'}
                                    value={formList?.description ? formList?.description : ''}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-header-center">
                    <button className="form-undo-redo-btn" onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)">
                        <img src={Images.undoForm} alt="undo" />
                    </button>
                    <button className="form-undo-redo-btn" onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
                        <img src={Images.redoForm} alt="redo" />
                    </button>
                    <div className="form-view-actions-wrapper">
                        <button
                            onClick={() => changeActiveTab(formTabEnums.BUILDER)}
                            className={activeTab === formTabEnums.BUILDER ? 'form-view-active-btn' : 'form-view-passive-btn'}>
                            <img src={Images.builder} alt="builder" />
                            <p>Builder</p>
                        </button>
                        <button
                            onClick={() => changeActiveTab(formTabEnums.PREVIEW)}
                            className={activeTab === formTabEnums.PREVIEW ? 'form-view-active-btn' : 'form-view-passive-btn'}>
                            <img src={Images.formPreview} alt="icon" />
                            <p>Preview</p>
                        </button>
                    </div>
                </div>

                <div className="form-header-right">
                    <a
                        style={{ textDecoration: 'underline' }}
                        className="learn-how"
                        href={'https://help.therapylake.com/forms/creating-a-new-form'}
                        target={'_blank'}
                        rel="noopener noreferrer">
                        Need help?
                    </a>

                    {formList?.id && (
                        <EditWithIcon
                            id={'download-form'}
                            icon={Images.historyTab}
                            type={'button'}
                            handleClick={() => setShowHistory(true)}
                        />
                        // <button
                        //     onClick={() => setShowHistory(true)}
                        //     className="learn-how"
                        //     style={{ background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                        // >
                        //     Version history
                        // </button>
                    )}

                    {formList?.model && <DataFields />}

                    <SimpleTooltip title={'Select where this template will be used (e.g. Staff, Client, Invoice Notes)'} placement="left" arrow>
                        <div style={{ width: 200, marginBottom: '-21px' }}>
                            <SelectTypeAutocomplete
                                disabled={checkDefault || formList?.id}
                                name={'model'}
                                handleSelect={handleChange}
                                defaultValue={formList?.model}
                                list={models ? transformNotesObject(models) : []}
                                placeholder={'Use Case *'}
                                renderValue={(i) => i?.name}
                                wideMenu
                                uiType={'tableFilter'}
                                error={error === 'all' ? 'model' : error}
                                typeError={error === 'model' || error === 'all' ? '  ' : ''}
                            />
                        </div>
                    </SimpleTooltip>

                    {!checkDefault && RolePermission([PermissionList.MANAGE_CUSTOM_FORM?.code]) && (
                        <div className="flex-align-center" style={{ gap: 16 }}>
                            <div className="flex-align-center" style={{ gap: 6 }}>
                                <p className="form-status-box">
                                    {formList?.status === firmStatusEnums.PUBLISHED ? 'Published' : 'Unpublished'}
                                </p>
                                <CustomizedSwitch
                                    checked={formList?.status === firmStatusEnums.PUBLISHED}
                                    handleClick={handleStatusToggle}
                                />
                            </div>

                            {formList?.title || formList?.model ? (
                                <button
                                    disabled={loader?.length || previewing}
                                    onClick={() => submitForm()}
                                    className="form-publish-btn"
                                    style={{ opacity: loader?.length || previewing ? 0.7 : 1 }}>
                                    {loader?.length ? <MinLoader margin={'0'} color="white" position={'relative'} /> : 'Save'}
                                </button>
                            ) : (
                                <SimpleTooltip title={'Complete all required fields before publishing'} placement="top" arrow>
                                    <button
                                        disabled={previewing}
                                        style={{ opacity: 0.5 }}
                                        onClick={() => submitForm()}
                                        className="form-publish-btn">
                                        Save
                                    </button>
                                </SimpleTooltip>
                            )}
                        </div>
                    )}
                </div>

                <VersionHistoryPanel
                    formId={formList?.id}
                    sections={formList?.sections}
                    open={showHistory}
                    onClose={() => setShowHistory(false)}
                    onRestored={onRestored}
                    onPreview={onPreview}
                    previewingVersion={previewing?.versionNumber}
                />
            </div>
        </>
    );
};
